import React from "react";
import {
  Avatar,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from "@mui/material";
import { indigo } from "@material-ui/core/colors";
import {
  activityUserLabel,
  avatarUserName,
  getCustomizedSummary,
  getDocumentUrl,
  getPatientName,
  getReferralUrl,
  getRelatedReferral,
  isSelfUser,
  isValidToDisplay,
} from "../../../utils";
import { ACTIVITY_TYPE_NAME } from "../../../constants";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import PropTypes from "prop-types";

const ObjectItemLink = ({ objectItem, clientId }) => {
  if (!objectItem) return null;

  const label = objectItem.name || "";

  switch (objectItem.typeName) {
    case ACTIVITY_TYPE_NAME.DOCSERVER_DOCUMENT:
    case ACTIVITY_TYPE_NAME.RECEIVED_FAX_DOCUMENT:
      return (
        <Link
          to={getDocumentUrl(objectItem, clientId)}
          target="_blank"
          rel="opener"
        >
          {label}
        </Link>
      );

    case ACTIVITY_TYPE_NAME.SERVICE_REQUEST:
      return (
        <Link
          to={`/service-requests/${clientId}/${objectItem.key}`}
          target="_blank"
          rel="opener"
        >
          {label}
        </Link>
      );

    case ACTIVITY_TYPE_NAME.SENTREFERRAL:
    case ACTIVITY_TYPE_NAME.RCVDREFERRAL:
      return <Link to={getReferralUrl(objectItem)}>{label}</Link>;

    default:
      return null;
  }
};

ObjectItemLink.propTypes = {
  objectItem: PropTypes.object,
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const isReferral = (item) =>
  item?.typeName === ACTIVITY_TYPE_NAME.SENTREFERRAL ||
  item?.typeName === ACTIVITY_TYPE_NAME.RCVDREFERRAL;

const isDocument = (item) =>
  item?.typeName === ACTIVITY_TYPE_NAME.DOCSERVER_DOCUMENT ||
  item?.typeName === ACTIVITY_TYPE_NAME.RECEIVED_FAX_DOCUMENT;

const DefaultActivityItem = ({ activity, selfId }) => {
  const { fields } = activity || {};
  const { objectItem, relatedItems, clientId, summary } = fields || {};

  const firstRelated = relatedItems?.[0] || null;

  const hasReferralDocPair =
    (isDocument(objectItem) && isReferral(firstRelated)) ||
    (isReferral(objectItem) && isDocument(firstRelated));

  const relatedReferralEl =
    relatedItems?.length > 0
      ? getRelatedReferral(relatedItems, clientId)
      : null;

  return (
    <React.Fragment key={`fragment_${activity.id}`}>
      <ListItem key={activity.id}>
        <ListItemAvatar>
          <Avatar
            sx={
              isSelfUser(activity, selfId)
                ? {
                    color: "#fff !important",
                    backgroundColor: `${indigo["A700"]} !important`,
                  }
                : {}
            }
          >
            {avatarUserName(activity)}
          </Avatar>
        </ListItemAvatar>

        <Grid container>
          <Grid item xs={12}>
            {Boolean(objectItem) && (
              <Typography>
                {`${activityUserLabel(activity, selfId)} ${getCustomizedSummary(
                  activity
                )}`}{" "}
                {hasReferralDocPair ? (
                  <>
                    {isReferral(objectItem) ? (
                      <>
                        <ObjectItemLink
                          objectItem={objectItem}
                          clientId={clientId}
                        />{" "}
                        <ObjectItemLink
                          objectItem={firstRelated}
                          clientId={clientId}
                        />
                      </>
                    ) : (
                      <>
                        <ObjectItemLink
                          objectItem={firstRelated}
                          clientId={clientId}
                        />{" "}
                        <ObjectItemLink
                          objectItem={objectItem}
                          clientId={clientId}
                        />
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <ObjectItemLink
                      objectItem={objectItem}
                      clientId={clientId}
                    />{" "}
                    {relatedReferralEl}
                  </>
                )}
              </Typography>
            )}

            {!objectItem && (
              <Typography>
                {`${activityUserLabel(activity, selfId)} ${(
                  summary || ""
                ).toLowerCase()}`}
              </Typography>
            )}
          </Grid>

          <Grid item xs={6}>
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontWeight: "500",
                lineHeight: "1.57",
                letterSpacing: "0.00714em",
              }}
              variant="subtitle2"
            >
              {getPatientName(fields)}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontWeight: "500",
                lineHeight: "1.57",
                letterSpacing: "0.00714em",
              }}
              color="textSecondary"
              variant="subtitle2"
            >
              {format(new Date(fields?.eventTime), "MMM d, y h:mm a")}
            </Typography>
          </Grid>
        </Grid>
      </ListItem>

      {fields?.changedValues && (
        <List key={`changelist_${activity.id}`} component="div" disablePadding>
          {fields.changedValues.map((change) => {
            const displayOptions = isValidToDisplay(change.fieldName);
            if (!displayOptions) return null;

            const displayFrom = displayOptions.displayFn
              ? displayOptions.displayFn(change.changedFrom)
              : change.changedFrom;
            const displayTo = displayOptions.displayFn
              ? displayOptions.displayFn(change.changedTo)
              : change.changedTo;

            if (
              (!displayFrom || displayFrom === "") &&
              (!displayTo || displayTo === "")
            ) {
              return null;
            }

            return (
              <ListItem
                id={`${activity.id}_change_${change.fieldName}`}
                key={change.fieldName}
              >
                {displayFrom &&
                  displayFrom !== "" &&
                  displayTo &&
                  displayTo !== "" && (
                    <ListItemText
                      primary={`${displayOptions.displayName} changed from "${displayFrom}" to "${displayTo}".`}
                    />
                  )}

                {displayFrom &&
                  displayFrom !== "" &&
                  (!displayTo || displayTo === "") && (
                    <ListItemText
                      primary={`${displayOptions.displayName} "${displayFrom}" was removed.`}
                    />
                  )}

                {(!displayFrom || displayFrom === "") &&
                  displayTo &&
                  displayTo !== "" && (
                    <ListItemText
                      primary={`${displayOptions.displayName} was set to "${displayTo}".`}
                    />
                  )}
              </ListItem>
            );
          })}
        </List>
      )}
    </React.Fragment>
  );
};

DefaultActivityItem.propTypes = {
  activity: PropTypes.object.isRequired,
  selfId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default DefaultActivityItem;
